import { inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { LogService } from './log.service';
import { SlidePlaylist } from './slide';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  Subject,
  Subscription,
  take,
  throwError
} from 'rxjs';
import { catchError } from 'rxjs/operators';
import { io, Socket } from 'socket.io-client';
import { EventconfigStoreService } from './eventconfig-store.service';

@Injectable({
  providedIn: 'root',
})
export class SlideStoreService {

  private http: HttpClient = inject(HttpClient);
  private es: EventconfigStoreService = inject(EventconfigStoreService);

  private nodeServiceUrl = environment.production ? "event-backend" : "test-event-backend";

  // NEW CODE
  private subscriptions: Subscription[] = [];
  // Slide Playlist
  private playlistSource = new ReplaySubject<SlidePlaylist[]>(1);
  public readonly playlist$ = this.playlistSource.asObservable();
  private currentPlaylist: SlidePlaylist[] = [];

  // Loading state (if needed)
  public readonly _isPendingSlideConfig = new BehaviorSubject<boolean>(true);
  public readonly isPendingSlideConfig$ = this._isPendingSlideConfig.asObservable();

  // Selected slide
  private selectedSlideSource = new BehaviorSubject<SlidePlaylist | null>(null);
  selectedSlide$ = this.selectedSlideSource.asObservable()

  // Scroll to slide
  private scrollToSlideSubject = new Subject<SlidePlaylist>();
  scrollToSlide$ = this.scrollToSlideSubject.asObservable();

  // Socket-related properties
  public socket: Socket;
  private socketConnected = new BehaviorSubject<boolean>(false);
  public socketConnected$ = this.socketConnected.asObservable();
  private SOCKET_ENDPOINT = environment.apiUrl;

  getSlidePlaylist() {
    this._isPendingSlideConfig.next(true);
    this.subscriptions.push(
      this.getSlidePlaylistFromApi().subscribe({
        next: (slidePlaylist: SlidePlaylist[]) => {
          // Emit different parts of the config to respective subjects
          this.currentPlaylist = slidePlaylist;
          this.playlistSource.next(slidePlaylist);
          this.selectedSlideSource.next(this.getDefaultSlide(slidePlaylist));
          this._isPendingSlideConfig.next(false);
        },
        error: (err) => {
          console.error('Error fetching slide config', err);
          this._isPendingSlideConfig.next(false);
        }
      })
    );
  }

  // Modify API method to return full SlideConfig
  getSlidePlaylistFromApi(): Observable<SlidePlaylist[]> {
    return this.http.get<SlidePlaylist[]>(`${environment.apiUrl}/v1/${this.nodeServiceUrl}/slidemanagement/getSlidePlaylist`)
      .pipe(
        take(1),
        catchError((err: HttpErrorResponse) => {
          console.error('API Error', err);
          return throwError(() => err);
        })
      );
  }

  // Initialize socket connection
  public initializeSocketConnection() {
    this.socket = io(this.SOCKET_ENDPOINT + 'event_watcher', {
      path: `/v1/${this.nodeServiceUrl}/socket/`,
      auth: {
        token: this.es.getInitCipher(),
        customer: environment.customerShort,
        instance: 'event'
      }
    });

    // Socket connection events
    this.socket.on('connect', () => {
      this.socketConnected.next(true);
    });

    this.socket.on('disconnect', () => {
      this.socketConnected.next(false);
    });

    // Listen for slide configuration updates
    this.setupSocketListeners();
  }

  private setupSocketListeners() {
    // Deck updates
    this.socket.on('update_slides_config', (slidesConfig: any) => {
      this.currentPlaylist = slidesConfig['playlist'];
      this.playlistSource.next(slidesConfig['playlist']);
      this.setSelectedSlide(this.getDefaultSlide(slidesConfig['playlist']));
    });

    // Error handling
    this.socket.on('error', (error: any) => {
      console.error('Socket error:', error);
    });
  }

  // Post-Live: erste Folie, sonst (Pre-Live/Live): aktuelle Live-Folie (letzte)
  private getDefaultSlide(playlist: SlidePlaylist[]): SlidePlaylist | undefined {
    if (!playlist?.length) {
      return undefined;
    }
    return this.es.getEventState() === 'post-live' ? playlist[0] : playlist[playlist.length - 1];
  }

  selectFirstSlide() {
    if (this.currentPlaylist?.length) {
      this.setSelectedSlide(this.currentPlaylist[0]);
    }
  }

  triggerScrollToSlide(slide: SlidePlaylist) {
    this.scrollToSlideSubject.next(slide);
  }

  setSelectedSlide(slide: SlidePlaylist) {
    // Only update if it's a different slide
    if (this.selectedSlideSource.getValue()?._id !== slide?._id) {
      this.selectedSlideSource.next(slide);

      // Optional: Add a small delay to prevent visual jumps
      setTimeout(() => {
        this.triggerScrollToSlide(slide);
      }, 100);
    }
  }

}
